// Smart backend URL detection for both preview and production environments
const getBackendURL = () => {
  // If explicitly set in .env, use it (for preview environment)
  const envURL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if we're in production (custom domain) vs preview/development
  const currentHost = window.location.hostname;
  const isPreviewOrDev = currentHost.includes('preview.emergentagent.com') || 
                         currentHost.includes('emergentagent.com') ||
                         currentHost === 'localhost' ||
                         currentHost === '127.0.0.1';
  
  // Check if this is an Emergent native deployment (*.emergent.host)
  const isEmergentHost = currentHost.includes('.emergent.host');
  
  // For preview/development environments, use env variable if available
  if (isPreviewOrDev && envURL) {
    console.log('[CONFIG] Using preview environment URL:', envURL);
    return envURL;
  }
  
  // For Emergent native deployments and custom domains, use same-origin
  const backendURL = window.location.origin;
  console.log('[CONFIG] Using same-origin URL:', backendURL, 'for host:', currentHost);
  return backendURL;
};

export const BACKEND_URL = getBackendURL();
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// App version baked in at build time from package.json (see craco.config.js, ADR-0008).
// Compare against GET /api/health "version" to detect a stale bundle.
export const APP_VERSION = process.env.REACT_APP_VERSION || "0.0.0-dev";

// Phase 1 feature flags (ADR-0009 §4). Names and defaults mirror backend/feature_flags.py.
// Three layers, resolved per flag:
//   1. FLAG_DEFAULTS below: the code default, identical to the backend's FLAG_DEFAULTS
//      (backend/tests/test_feature_flags_env.py fails if they drift);
//   2. build time: REACT_APP_FLAGS (baked by craco.config.js from TAPPD_FLAGS) is a FLOOR:
//      a baked flag is ON in this bundle whatever the defaults or the backend say. So the
//      backend kill switch (TAPPD_FLAG_<NAME>=false) cannot reach a flag baked here:
//      leave TAPPD_FLAGS empty in production (docs/guides/cutover-runbook.md);
//   3. runtime: GET /api/flags "flags" (the backend's effective set: code default plus
//      TAPPD_FLAG_<NAME> env), applied by loadRuntimeFlags() which index.js awaits before
//      first render. It is AUTHORITATIVE in both directions for every unbaked flag: a name
//      in the list is on, a name missing from it is off (so the backend can turn a
//      default-on flag off). If it does not arrive in time (timeout, network error, non-2xx,
//      bad JSON, no "flags" array) the app renders with layers 1 + 2, never "all off".
// flag() stays synchronous: read it during render, not at module load.
//
// CUTOVER SWITCHES: SLOT_MODEL and COASTER_STUDIO are true since their cutover flips, set
// with the matching backend defaults. Their TAPPD_FLAG_<NAME>=false kill switches turn them
// off through layer 3 when /api/flags answers in time; on a timeout the bundle falls back to
// these defaults (on).
export const FLAG_DEFAULTS = Object.freeze({
  REWARDS: false,
  INSTANT_REDEEM: false,
  COASTER_INVENTORY: false,
  GOOGLE_RATINGS_SYNC: false,
  PITCH_DECK: false,
  COASTER_GALLERY: false,
  VENUE_ANALYTICS: false,
  MULTI_BUSINESS: false,
  CREDENTIAL_CHANGE: false,
  EXTRA_FORMS: false,
  SLOT_MODEL: true,
  SLOT_BILLING: false,
  COASTER_STUDIO: true,
});
export const FLAG_NAMES = Object.keys(FLAG_DEFAULTS);
const parseFlagList = (list) => (Array.isArray(list) ? list : String(list || '').split(','))
  .map((f) => String(f).trim().toUpperCase())
  .filter(Boolean);
const buildTimeFlags = new Set(parseFlagList(process.env.REACT_APP_FLAGS));
// Mutable on purpose: loadRuntimeFlags() sets entries in place so every importer sees them.
export const FLAGS = Object.fromEntries(FLAG_NAMES.map((n) => [n, FLAG_DEFAULTS[n] || buildTimeFlags.has(n)]));
export const flag = (name) => !!FLAGS[String(name).toUpperCase()];
if (typeof window !== "undefined") {
  window.__TAPPD_FLAGS__ = FLAGS;
}

export const RUNTIME_FLAGS_TIMEOUT_MS = 1500;

/**
 * Fetch the backend's enabled flags from /api/flags (DB-free) and apply them: every
 * known flag becomes (in the runtime list) OR (baked at build time).
 * Never rejects: on network error, bad JSON, non-2xx, a body without a "flags" array or
 * timeout it resolves without changing anything (defaults + build-time flags apply).
 * A response landing after the timeout is ignored (first render already happened).
 * @returns {Promise<string[]>} names whose value this call changed (on or off)
 */
export const loadRuntimeFlags = ({ timeoutMs = RUNTIME_FLAGS_TIMEOUT_MS, fetchImpl } = {}) => {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return Promise.resolve([]);
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  let timedOut = false;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      timedOut = true; // a late response must not flip flags after first render
      if (controller) controller.abort();
      resolve([]);
    }, timeoutMs);
  });
  const load = Promise.resolve()
    .then(() => doFetch(`${BACKEND_URL}/api/flags`, {
      cache: 'no-store',
      ...(controller ? { signal: controller.signal } : {}),
    }))
    .then((res) => (res && res.ok ? res.json() : null))
    .then((body) => {
      const changed = [];
      if (timedOut || !body || !Array.isArray(body.flags)) return changed;
      const runtime = new Set(parseFlagList(body.flags));
      FLAG_NAMES.forEach((name) => {
        const next = runtime.has(name) || buildTimeFlags.has(name);
        if (FLAGS[name] !== next) {
          FLAGS[name] = next;
          changed.push(name);
        }
      });
      return changed;
    })
    .catch(() => []);
  return Promise.race([load, timeout]).finally(() => clearTimeout(timer));
};
if (typeof window !== "undefined") {
  window.__TAPPD_VERSION__ = APP_VERSION;
}

/**
 * Resolve asset URL - handles both local paths and absolute CDN URLs
 * @param {string} url - The URL to resolve (can be /api/files/... or https://cdn...)
 * @returns {string} - The full URL ready for use in img src, etc.
 */
export const resolveAssetUrl = (url) => {
  if (!url) return '';
  
  // If already an absolute URL (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Otherwise, prepend the backend URL for local paths like /api/files/...
  return `${BACKEND_URL}${url}`;
};
